import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO, generateLocalBusinessSchema } from '../components/SEO';
import { CTASection } from '../components/CTASection';
import { GoogleMapsSection } from '../components/GoogleMapsSection';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQSection } from '../components/FAQSection';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { neighborhoods, testimonials, faqs, services, beforeAfterGallery } from '../data/mock';
import { MapPin, Star, Navigation } from 'lucide-react';

export const LavaJatoPertoDeMim = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = generateLocalBusinessSchema({
    aggregateRating: {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": testimonials.length
    },
    "areaServed": [
      { "@type": "City", "name": "Goiânia" },
      { "@type": "City", "name": "Aparecida de Goiânia" }
    ]
  });

  const slugify = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-');
  };

  return (
    <>
      <SEO 
        title="Lava Jato Perto de Mim em Goiânia e Aparecida - 18 Bairros Atendidos | Box7go"
        description="Procurando lava jato perto de você em Goiânia e Aparecida de Goiânia? A Box7go atende 18 bairros com serviços premium. Veja localização e agende agora!"
        keywords="lava jato perto de mim, lava jato próximo goiania, lava jato goiania, lava jato aparecida de goiania, estética automotiva perto de mim"
        schemaData={schemaData}
      />

      {/* Hero */}
      <section className="relative min-h-[500px] flex items-center bg-black">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1761312834150-4beefff097a7)`,
            opacity: 0.4
          }}
        ></div>
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Lava Jato Perto de Você em Goiânia
            </h1>
            <p className="text-xl text-white/90 mb-8">
              A Box7go está estrategicamente localizada para atender você com rapidez e qualidade premium. Atendemos 18 bairros em Goiânia e Aparecida de Goiânia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <WhatsAppButton 
                text="Solicitar Orçamento"
                className="text-lg px-8 py-4"
              />
              <a
                href="https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-black font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105"
              >
                <Navigation className="w-5 h-5" />
                Como Chegar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bairros Atendidos */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Atendemos sua região
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Lava jato e estética automotiva de qualidade premium próximo a você
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {neighborhoods.map((neighborhood) => (
              <div key={neighborhood} className="bg-gray-50 rounded-lg p-6 hover:bg-[#00B4D8] hover:text-white transition-all duration-300 group">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#00B4D8] group-hover:text-white flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">{neighborhood}</h3>
                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/lava-jato-${slugify(neighborhood)}`}
                        className="text-sm hover:underline"
                      >
                        Lava Jato {neighborhood}
                      </Link>
                      <Link
                        to={`/estetica-automotiva-${slugify(neighborhood)}`}
                        className="text-sm hover:underline"
                      >
                        Estética Automotiva {neighborhood}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços Rápidos */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serviços Disponíveis
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Do pit stop ao detalhamento premium, escolha o serviço ideal
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.slice(0, 4).map((service) => (
              <Link
                key={service.id}
                to={`/${service.slug}`}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-32">
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-1">{service.name}</h3>
                  <p className="text-sm text-gray-600">{service.shortDesc}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold hover:text-[#0096C7] transition-colors"
            >
              Ver todos os serviços →
            </Link>
          </div>
        </div>
      </section>

      {/* Antes e Depois */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Resultados Comprovados
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Veja a transformação que fazemos em veículos da sua região
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {beforeAfterGallery.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img 
                      src={item.before} 
                      alt={`${item.title} - Antes`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute bottom-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                      ANTES
                    </div>
                  </div>
                  <div className="relative">
                    <img 
                      src={item.after} 
                      alt={`${item.title} - Depois`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                      DEPOIS
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-gray-900 text-sm">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avaliações */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Clientes da sua região recomendam
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Avaliações reais de moradores de Goiânia e Aparecida de Goiânia
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Mapa */}
      <GoogleMapsSection />

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection 
        title="Encontrou o lava jato mais próximo?"
        subtitle="Agende agora pelo WhatsApp e garanta seu horário"
      />
    </>
  );
};
